<template lang="pug">
#event-list
  .box
    .box-header
      .d-flex.flex-row
        .active-count.d-flex.flex-column.flex-fill
          span ご家族に公開するイベント情報を登録できます。登録後に非公開を公開中に変更してください。
          span 公開数 {{ activeCount }} / {{ maxActiveNum }} （公開可能なイベントは{{ maxActiveNum }}件までです）
          span.error-message.p-0 公開中のイベント情報の修正は非公開に変更してから行ってください。
        .buttons.d-flex.flex-row-reverse.justify-content-end.flex-wrap
          button.btn.float-right.add-btn(@click="gotoEventRegister") 追加
    .box-body
      b-tabs(v-model="tabIndex")
        //- public events
        b-tab.tab
          template(v-slot:title)
            .tab-title
              span.mr-1 公開中のみ
              .refresh-icon(@click.stop="refresh" title="再読み込みします")
          PublicEventTab(ref="publicEventTab"
            :isLoading="isLoading" :items="publicItems" :rows.sync="publicEventCount"
            @onPreview="showPreview" @onStatusSwitch="confirmUpdateEventStatusModal" @onEdit="gotoEventEdit" @onDelete="showConfirmDeleteModal"
            @onApplicationStatus="showApplicationStatus" @onLiveStart="startLive")
        //- non public events
        b-tab.tab
          template(v-slot:title)
            .tab-title
              span.mr-1 非公開のみ
              .refresh-icon(@click.stop="refresh" title="再読み込みします")
          NonPublicEventTab(ref="nonPublicEventTab"
            :isLoading="isLoading" :items="nonPublicItems" :rows.sync="nonPublicEventCount"
            @onPreview="showPreview" @onStatusSwitch="confirmUpdateEventStatusModal" @onEdit="gotoEventEdit" @onDelete="showConfirmDeleteModal"
            @onApplicationStatus="showApplicationStatus" @onLiveStart="startLive")
        //- all events
        b-tab.tab(title="全て")
          template(v-slot:title)
            .tab-title
              span.mr-1 全て
              .refresh-icon(@click.stop="refresh" title="再読み込みします")
          ALlEventTab(ref="allEventTab"
            :isLoading="isLoading" :items="items" :rows.sync="eventCount"
            @onPreview="showPreview" @onStatusSwitch="confirmUpdateEventStatusModal" @onEdit="gotoEventEdit" @onDelete="showConfirmDeleteModal"
            @onApplicationStatus="showApplicationStatus" @onLiveStart="startLive")

  //- update status modal
  OkCancelModal(v-model="showUpdateEventStatusModal" :title="updateEventStatusMessage" @ok="updateEventStatus")
  //- delete event modal
  OkCancelModal(v-model="showDeleteModal" :title="deleteMessage" @ok="deleteEvent")
  OkModal(v-model="showDeleteSuccessModal" title="削除に成功しました。")
  //- delete event reply modal
  OkCancelModal(v-model="showEventReplyDeleteModal" :title="deleteEventReplyMessage" @ok="deleteEventReply")
  OkModal(v-model="showEventReplyDeleteSuccessModal" title="削除に成功しました。")
  //- preview modal
  EventPreviewModal(ref="eventPreviewModal" :event="previewTargetEvent" :imageUrl="imageUrl")
  //- replies modal
  EventRepliesModal(v-model="showEventRepliesModal" :items="eventReplyItems" :title="eventReplyTitle" :isExpired="isExpired" 
    @editEventReplies="gotoEventRepliesEdit" @deleteEventReplies="showDeleteEventRepliesConfirm" @downloadCsv="exportCsv" 
    @gotoEventReplyRegister="gotoEventReplyRegister")
  //- error message modal
  OkModal(v-model="showErrorMessageModal" :title="errorMessage")
  //- all page layer loading
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Ref, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { tabIndexesStore } from "@/store/store";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";
import EventsService, { EventGetModel, EventTargetType, EventPatchModel } from "@/commons/services/Events";
import EventRepliesService, { AttendType, EventRepliesGetModel } from "@/commons/services/EventReplies";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import S3UrlService from "@/commons/services/S3Url";
import PublicS3UrlToBase64 from "@/commons/utils/PublicS3UrlToBase64";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import EventPreviewModal from "@/pc/components/modals/EventPreviewModal.vue";
import { EventTableItem } from "@/pc/containers/events/EventTable.vue";
import EventRepliesModal, { EventRepliesTableModel } from "@/pc/containers/events/EventRepliesModal.vue";
import PublicEventTab from "@/pc/containers/events/PublicEventTab.vue";
import NonPublicEventTab from "@/pc/containers/events/NonPublicEventTab.vue";
import ALlEventTab from "@/pc/containers/events/AllEventTab.vue";

import eventDefaultImage from "@/assets/event-default.png";

@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal,
    EventRepliesModal,
    EventPreviewModal,
    PublicEventTab,
    NonPublicEventTab,
    ALlEventTab
  },
  name: "EventList"
})
export default class EventList extends Vue {
  /** イベントサービス */
  @Inject("EventsService") eventsService!: EventsService;
  /** イベント返信サービス */
  @Inject("EventRepliesService") eventRepliesService!: EventRepliesService;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  @Ref()
  publicEventTab!: PublicEventTab;
  @Ref()
  nonPublicEventTab!: NonPublicEventTab;
  @Ref()
  allEventTab!: ALlEventTab;

  @Ref()
  eventPreviewModal!: EventPreviewModal;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 部分ローディング表示可否 */
  isLoading: boolean = false;

  /** エラーメッセージ表示モーダル */
  showErrorMessageModal: boolean = false;
  /** エラーメッセージ */
  errorMessage: string = "";

  /** 公開イベント数 */
  activeCount: number = 0;
  /** 公開イベント数の上限。設定情報から取得する */
  maxActiveNum: number = Setting.event.maxActiveCount;

  /** 公開・非公開の更新確認モーダル表示可否 */
  showUpdateEventStatusModal: boolean = false;
  /** 公開・非公開の更新確認モーダルタイトル */
  updateEventStatusMessage: string = "";
  /** 公開・非公開ボタン押下時の公開設定更新対象イベント */
  updateEventStatusTargetEvent: EventTableItem | null = null;

  /** イベント削除確認モーダル表示可否 */
  showDeleteModal: boolean = false;
  /** イベント削除確認モーダルメッセージ */
  deleteMessage: string = "";
  /** イベント削除対象のイベント */
  deleteTargetEventItem: EventTableItem | null = null;
  /** イベント削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;

  /** イベントプレビュー対象イベント */
  previewTargetEvent: EventTableItem | null = null;
  /** イベントプレビュー画像URL */
  imageUrl: string = "";
  /** イベントの画像が無い場合に表示するデフォルト画像(施設の画像) */
  defaultImage: string = eventDefaultImage;

  /** イベント返信モーダル表示可否 */
  showEventRepliesModal: boolean = false;
  /** イベント返信モーダル表示情報 */
  eventReplyItems: Array<EventRepliesTableModel> = [];
  /** イベント返信モーダルタイトル */
  eventReplyTitle: string = "";
  /** イベント返信モーダルで「代理参加申込みボタン」を表示するか否か */
  isExpired: boolean = true;
  /** イベント返信モーダルを表示するイベントID */
  eventRepliesEventId: number = 0;

  /** イベント返信削除確認モーダル表示可否 */
  showEventReplyDeleteModal: boolean = false;
  /** 削除モーダルメッセージ */
  deleteEventReplyMessage: string = "";
  /** 削除対象のイベント返信ソートキー */
  deleteTargetEventReplySortKey: string = "";
  /** イベント返信削除完了モーダル表示可否 */
  showEventReplyDeleteSuccessModal: boolean = false;

  /** 現在表示しているタブ番号 */
  tabIndex: number = 0;

  /** 公開イベント */
  publicItems: Array<EventTableItem> = [];
  /** 公開イベント行数 */
  publicEventCount: number = 0;
  /** 非公開イベント */
  nonPublicItems: Array<EventTableItem> = [];
  /** 非公開イベント行数 */
  nonPublicEventCount: number = 0;
  /** 全てのイベント */
  items: Array<EventTableItem> = [];
  /** 全てのイベント行数 */
  eventCount: number = 0;

  /**
   * vue lifecycle created
   */
  public async created(): Promise<void> {
    this.activeCount = await this.getActiveCount();
    return;
  }

  /**
   * 公開イベント数を取得する
   */
  public async getActiveCount(): Promise<number> {
    const params: any = {
      status: "public"
    };
    const count: number = await this.eventsService.getCount(userStore, params);
    return count;
  }

  /**
   * ### vue lifecycle mounted
   *
   * - タブ番号を復元後、初期値に戻す
   * - 画面読込みは非同期で行われるので、完了を待ってからタブ番号を復元する
   */
  public async mounted(): Promise<void> {
    this.$nextTick(() => {
      this.tabIndex = tabIndexesStore.eventTabIndex;
      tabIndexesStore.setEventTabIndex({ index: 0 });
    });

    await this.initialize();

    return;
  }

  /**
   * ### 画面初期化
   */
  public async initialize(): Promise<void> {
    this.isLoading = true;

    this.publicItems = [];
    this.nonPublicItems = [];
    this.items = [];

    await this.createView();

    this.isLoading = false;

    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    // 画面表示で使用する利用者グループ情報と取得し、MAPにする
    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const residentGroupNameMap: Map<number, string> = this.createResidentGroupNameMap(groups);

    const events: Array<EventGetModel> = await this.getEvents();
    const items: Array<EventTableItem> = await this.createItems(events, residentGroupNameMap);
    this.setEventItems(items);

    this.setPublicEventsAttendeeCountAndImageUrl();
    this.setNonPublicEventsAttendeeCountAndImageUrl();
    this.setAllEventsAttendeeCountAndImageUrl();

    return;
  }

  /**
   * ### 利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const groups: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return groups;
  }

  /**
   * ### 利用者グループ名MAPを作成する
   */
  public createResidentGroupNameMap(residentGroups: Array<ResidentGroupsGetModel>): Map<number, string> {
    let residentGroupNameMap: Map<number, string> = new Map<number, string>();
    for (const group of residentGroups) {
      residentGroupNameMap.set(group.id, group.name);
    }
    return residentGroupNameMap;
  }

  /**
   * ### イベントを取得する
   */
  public async getEvents(): Promise<Array<EventGetModel>> {
    let response: Array<EventGetModel> = [];

    try {
      let params: any = {
        sortKey: "timeFrom",
        order: "desc",
        needFiltering: "true"
      };
      response = await this.eventsService.gets(userStore, params);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const params: any = {
          needFiltering: "true"
        };
        const count: number = await this.eventsService.getCount(userStore, params);
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * 300,
            endIndex: i * 300 + 300,
            sortKey: "timeFrom",
            order: "desc",
            needFiltering: "true"
          };
          response = response.concat(await this.eventsService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### イベントの行を作成する
   */
  public async createItems(events: Array<EventGetModel>, residentGroupNameMap: Map<number, string>): Promise<Array<EventTableItem>> {
    let items: Array<EventTableItem> = [];

    for (const event of events) {
      let targets: string[] = [];

      if (EventTargetType.RESIDENT_GROUP <= event.targetType) {
        for (const target of event.targets) {
          const groupName: string | undefined = residentGroupNameMap.get(target);
          if (groupName != undefined) {
            targets.push(groupName);
          }
        }
      } else {
        targets.push("施設全体");
      }

      items.push({
        id: event.id,
        date: event.date,
        datetime: {
          date: this.$dateUtil.convertNumberToString(event.date, "yyyy年mm月dd日"),
          day: event.dayOfWeek,
          timeFrom: this.$dateUtil.convertNumberToString(event.timeFrom, "hh:MM"),
          timeTo: this.$dateUtil.convertNumberToString(event.timeTo, "hh:MM")
        },
        timeFrom: event.timeFrom,
        timeTo: event.timeTo,
        targets: targets,
        place: event.place,
        isPublic: event.isPublic,
        title: event.title,
        description: event.description,
        imageUrl: event.imageUrl,
        convertedImageUrl: "",
        imageLoading: true,
        holdType: event.holdType,
        needReply: event.needReply,
        replyDeadline: event.replyDeadline as number,
        attendeeCount: undefined,
        meetingToken: event.meetingToken ? event.meetingToken : "",
        writtenBy: event.writtenBy,
        writerId: event.writerId,
        updated: event.updated
      });
    }

    return items;
  }

  /**
   * ### 各タブで表示するイベントを振り分ける
   */
  public setEventItems(items: Array<EventTableItem>): void {
    // 全てのイベントはitemsをコピーして使用する
    this.items = items.concat();
    this.eventCount = items.length;

    // 公開、非公開イベントを振り分ける
    let publicItems: Array<EventTableItem> = [];
    let nonPublicItems: Array<EventTableItem> = [];
    for (const item of items) {
      if (item.isPublic == true) {
        publicItems.push(item);
      } else {
        nonPublicItems.push(item);
      }
    }
    this.publicItems = publicItems;
    this.publicEventCount = this.publicItems.length;
    this.nonPublicItems = nonPublicItems;
    this.nonPublicEventCount = this.nonPublicItems.length;

    return;
  }

  /**
   * ### 公開のみタブの表示対象のイベントの返信件数、画像URLを格納する
   */
  public async setPublicEventsAttendeeCountAndImageUrl(): Promise<void> {
    const startIndex: number = (this.publicEventTab.currentPage - 1) * this.publicEventTab.perPage;
    const endIndex: number = Math.min(startIndex + this.publicEventTab.perPage, this.items.length);
    this.setAttendeeCountAndImageUrl(this.publicItems, startIndex, endIndex);
    return;
  }

  /**
   * ### 非公開のみタブの表示対象のイベントの返信件数、画像URLを格納する
   */
  public async setNonPublicEventsAttendeeCountAndImageUrl(): Promise<void> {
    const startIndex: number = (this.nonPublicEventTab.currentPage - 1) * this.nonPublicEventTab.perPage;
    const endIndex: number = Math.min(startIndex + this.nonPublicEventTab.perPage, this.items.length);
    this.setAttendeeCountAndImageUrl(this.nonPublicItems, startIndex, endIndex);
    return;
  }

  /**
   * ### 全てタブの表示対象のイベントの返信件数、画像URLを格納する
   */
  public async setAllEventsAttendeeCountAndImageUrl(): Promise<void> {
    const startIndex: number = (this.allEventTab.currentPage - 1) * this.allEventTab.perPage;
    const endIndex: number = Math.min(startIndex + this.allEventTab.perPage, this.items.length);
    this.setAttendeeCountAndImageUrl(this.items, startIndex, endIndex);
    return;
  }

  /**
   * ### イベントの返信件数、画像URLを格納する
   */
  public async setAttendeeCountAndImageUrl(items: Array<EventTableItem>, startIndex: number, endIndex: number): Promise<void> {
    let requestPromises: Array<Promise<void>> = [];
    for (let i = startIndex; i < endIndex; i++) {
      if (items[i] == undefined) {
        break;
      }

      if (items[i].attendeeCount == undefined) {
        requestPromises.push(this.setAttendeeCount(items[i]));
      }
      if (items[i].imageLoading == true) {
        requestPromises.push(this.convertImage(items[i]));
      }
    }

    Promise.all(requestPromises);

    return;
  }

  /**
   * ### 非同期でイベントの返信件数を取得して格納する
   */
  public setAttendeeCount(item: EventTableItem): Promise<void> {
    return new Promise(async (resolve: Function, reject: Function) => {
      try {
        const eventReplies: Array<EventRepliesGetModel> = await this.getEventReplies(item);
        item.attendeeCount = eventReplies.length;
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * ### 非同期で画像URLを変換する
   */
  public convertImage(item: EventTableItem): Promise<void> {
    return new Promise(async (resolve: Function, reject: Function) => {
      try {
        const imageUrl: string = item.imageUrl;
        const convertedImageUrl: string = await this.convertImageUrl(imageUrl);
        item.convertedImageUrl = convertedImageUrl;
        item.imageLoading = false;
        this.$forceUpdate();
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * ### DBに保存されているS3URLを公開状態にし、公開状態にした画像URLからDataURLを作って返す
   */
  public async convertImageUrl(imageUrl: string): Promise<string> {
    // 画像のURLがない場合は空を返す
    if (!imageUrl) {
      return "";
    }

    // 画像URL取得
    const convertImageUrl = await this.s3UrlService.get(userStore, Setting.backets.imageExpireHour, imageUrl);

    // イベント公開/非公開をする場合にAPIに画像をDataURL変換したものを送る
    // しかし、画面表示時にS3公開URLをimg要素が表示してしまうと、URLがChromeにキャッシュされる
    // イベント公開/非公開のタイミングで再度公開URLにアクセスして、img要素からcanvas要素に変換するタイミングで
    // キャッシュしたURLが使用されてCORSエラーになってしまう
    // 回避するために画面表示タイミングでDataURLに変換して使いまわすようにする
    const dataUrl: string = await PublicS3UrlToBase64.convert(convertImageUrl, "image/jpeg");

    return dataUrl;
  }

  /**
   * ### vue lifecycle activated
   */
  public async activated(): Promise<void> {
    if (needRefreshesStore.eventList.eventList == true) {
      await this.createView();
      needRefreshesStore.eventList.eventList = false;
    }
    return;
  }

  /**
   * ### 画面情報を更新する
   */
  public async refresh(): Promise<void> {
    await this.initialize();
    return;
  }

  /**
   * ### イベント登録画面へ遷移
   */
  public gotoEventRegister(): void {
    tabIndexesStore.setEventTabIndex({ index: this.tabIndex });
    this.$router.push(PcUrl.EventRegister);
    return;
  }

  /**
   * ### 公開/非公開のスイッチ押下
   */
  public confirmUpdateEventStatusModal(item: EventTableItem): void {
    // 公開可能最大数を超える場合、メッセージを表示して中断する
    if (!item.isPublic && this.maxActiveNum <= this.activeCount) {
      this.errorMessage = "公開可能最大数を超えているため、イベント情報を公開できません。";
      this.showErrorMessageModal = true;
      return;
    } else {
      this.errorMessage = "";
    }

    // 公開・非公開で表示するメッセージを分岐する
    if (item.isPublic) {
      this.updateEventStatusMessage = `${item.datetime.date}(${item.datetime.day})の${item.title}のイベント情報を非公開にしますか？`;
    } else {
      this.updateEventStatusMessage = `${item.datetime.date}(${item.datetime.day})の${item.title}のイベント情報を公開しますか？`;
    }
    this.updateEventStatusTargetEvent = item;

    this.showUpdateEventStatusModal = true;

    return;
  }

  /**
   * ### 介護記録の公開・非公開を更新する
   */
  public async updateEventStatus(): Promise<void> {
    try {
      this.isBusy = true;
      this.loadingMessage = "更新中...";

      // 更新する値を設定し、更新実施
      // APIのPATCHが部分更新ではなく完全更新で実装されているので、画像は変換する必要がある
      const event: EventTableItem = this.updateEventStatusTargetEvent as EventTableItem;

      event.isPublic = !event.isPublic;

      // 画像のdataURL変換は非同期なので、変換前であればここで変換する
      if (event.convertedImageUrl) {
        event.imageUrl = event.convertedImageUrl;
      } else {
        const convertedImageUrl: string = await this.convertImageUrl(event.imageUrl);
        event.imageUrl = convertedImageUrl;
      }

      // 更新対象のイベントを取得し、更新リクエストボディを作成する
      const updateTargetEvent: EventGetModel = await this.getEvent(event.id);
      const requestBody: EventPatchModel = this.createRequestBody(event, updateTargetEvent);

      await this.eventsService.patch(userStore, event.id, requestBody);

      // 公開・非公開の変更で表示対象のタブが変更になる＆全てタブの並び順が変更になる可能性がある
      if (event.isPublic == true) {
        this.publicItems = this.insertEventItem(this.publicItems, event);
        this.publicEventCount = this.publicItems.length;
        this.nonPublicItems = this.spliceEventItem(this.nonPublicItems, event.id);
        this.nonPublicEventCount = this.nonPublicItems.length;
      } else {
        this.publicItems = this.spliceEventItem(this.publicItems, event.id);
        this.publicEventCount = this.publicItems.length;
        this.nonPublicItems = this.insertEventItem(this.nonPublicItems, event);
        this.nonPublicEventCount = this.nonPublicItems.length;
      }
      this.items = this.publicItems.concat(this.nonPublicItems);

      // 公開イベント数を変更する
      if (event.isPublic) {
        this.activeCount++;
      } else {
        this.activeCount--;
      }
    } catch (error) {
      const systemError: SystemError = error as SystemError;

      // ステータスコードが400の場合、公開可能イベント数超過またはオンラインイベント数制限エラーなのでエラーメッセージを表示し画面情報を再取得する
      if (systemError.statusCode == 400 && 0 < systemError.originalResponse.indexOf("event publish limit over.")) {
        this.initialize();
        this.errorMessage = "公開可能最大数を超えているため、イベント情報を公開できません。";
        this.showErrorMessageModal = true;

        // 画面で管理しているイベント公開数とサーバー上のイベント公開数が不整合になっているのでここで再取得する
        this.activeCount = await this.getActiveCount();
      }
      if (systemError.statusCode == 400 && 0 < systemError.originalResponse.indexOf("online event already exists.")) {
        this.initialize();
        this.errorMessage = "オンラインイベントは同月に1イベントのみしか公開できません。";
        this.showErrorMessageModal = true;
      }
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      else if (systemError.statusCode == 409) {
        this.initialize();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
        this.showErrorMessageModal = true;
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 更新対象のイベント取得
   * TODO これ不要にしたい
   */
  public async getEvent(eventId: number): Promise<EventGetModel> {
    const response: EventGetModel = await this.eventsService.get(userStore, eventId);
    return response;
  }

  /**
   * ### イベント更新用のリクエストボディを作成する
   */
  public createRequestBody(event: EventTableItem, updateTargetEvent: EventGetModel): EventPatchModel {
    let replyDeadline: number | undefined = undefined;
    if (updateTargetEvent.needReply == true) {
      replyDeadline = updateTargetEvent.replyDeadline;
    }

    const requestBody: EventPatchModel = {
      isPublic: event.isPublic,
      date: updateTargetEvent.date,
      timeFrom: updateTargetEvent.timeFrom,
      timeTo: updateTargetEvent.timeTo,
      targetType: updateTargetEvent.targetType,
      targets: updateTargetEvent.targets,
      needReply: updateTargetEvent.needReply,
      replyDeadline: replyDeadline,
      holdType: updateTargetEvent.holdType,
      place: updateTargetEvent.place,
      title: updateTargetEvent.title,
      description: updateTargetEvent.description,
      image: event.imageUrl,
      meetingToken: event.meetingToken,
      writtenBy: userStore.loginUser.name,
      writerId: userStore.loginUser.userId,
      updated: updateTargetEvent.updated
    };

    return requestBody;
  }

  public spliceEventItem(items: Array<EventTableItem>, targetId: number): Array<EventTableItem> {
    return items.concat().filter((item: EventTableItem) => {
      if (item.id == targetId) {
        return false;
      } else {
        return true;
      }
    });
  }

  public insertEventItem(items: Array<EventTableItem>, targetItem: EventTableItem): Array<EventTableItem> {
    const insertedItems: Array<EventTableItem> = items.concat([targetItem]);
    return this.sortEventItems(insertedItems);
  }

  public sortEventItems(items: Array<EventTableItem>): Array<EventTableItem> {
    return items.concat().sort((a, b) => {
      if (a.date < b.date) {
        return 1;
      }
      if (a.date > b.date) {
        return -1;
      }
      return 0;
    });
  }

  /**
   * ### 修正画面へ遷移する
   */
  public gotoEventEdit(item: EventTableItem): void {
    tabIndexesStore.setEventTabIndex({ index: this.tabIndex });
    this.$router.push({ name: PcUrl.EventEdit, params: { id: `${item.id}` } });
    return;
  }

  /**
   * ### 削除確認モーダルを表示する
   */
  public showConfirmDeleteModal(item: EventTableItem): void {
    this.deleteMessage = `${item.datetime.date} ${item.title} のイベント情報を削除しますか？`;
    this.showDeleteModal = true;
    if (item.id) {
      this.deleteTargetEventItem = item;
    }
    return;
  }

  /**
   * ### イベント削除
   */
  public async deleteEvent(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    const deleteTargetEventItem: EventTableItem = this.deleteTargetEventItem as EventTableItem;
    await this.eventsService.delete(userStore, deleteTargetEventItem.id);

    // 公開・非公開かで対象のタブからitemを削除するだけで再リクエストはしない
    if (deleteTargetEventItem.isPublic == true) {
      this.publicItems = this.spliceEventItem(this.publicItems, deleteTargetEventItem.id);
      this.publicEventCount = this.publicItems.length;
      // 公開イベントを消したので公開イベント数を減算する
      this.activeCount--;
    } else {
      this.nonPublicItems = this.spliceEventItem(this.nonPublicItems, deleteTargetEventItem.id);
      this.nonPublicEventCount = this.nonPublicItems.length;
    }
    // 全てタブからitemを削除するだけで再リクエストはしない
    this.items = this.spliceEventItem(this.items, deleteTargetEventItem.id);
    this.eventCount = this.items.length;

    this.isBusy = false;
    this.showDeleteSuccessModal = true;

    return;
  }

  /**
   * ### イベントプレビューを表示する
   */
  public showPreview(item: EventTableItem): void {
    const imageUrl = item.convertedImageUrl ? item.convertedImageUrl : this.defaultImage; // URLが無い場合はデフォルト画像(施設の画像)を表示する
    this.imageUrl = imageUrl;
    this.previewTargetEvent = item;
    this.eventPreviewModal.show();
    return;
  }

  /**
   * ### 申込状況確認ボタン押下
   */
  public async showApplicationStatus(item: EventTableItem): Promise<void> {
    this.isBusy = true;
    this.showEventRepliesModal = true;

    this.eventRepliesEventId = item.id;
    this.eventReplyTitle = `${item.title} 参加者一覧`;

    const today: string = this.$dateUtil.convertDateToString(new Date(), "yyyymmdd");
    const date: number = Number(today + "000000000");
    if (date <= item.date) {
      this.isExpired = false;
    } else {
      this.isExpired = true;
    }

    const eventReplies: Array<EventRepliesGetModel> = await this.getEventReplies(item);
    const eventReplyItems: Array<EventRepliesTableModel> = this.createEventReplyItems(eventReplies);
    this.eventReplyItems = eventReplyItems;

    this.isBusy = false;

    return;
  }

  /**
   * ### 申込状況を取得
   */
  public async getEventReplies(item: EventTableItem): Promise<Array<EventRepliesGetModel>> {
    const eventReplies: Array<EventRepliesGetModel> = await this.eventRepliesService.gets(userStore, item.id);
    return eventReplies;
  }

  /**
   * ### 申し込み状況の表示情報を作成する
   */
  public createEventReplyItems(eventReplies: Array<EventRepliesGetModel>): Array<EventRepliesTableModel> {
    let eventReplyItems: Array<EventRepliesTableModel> = [];

    for (const reply of eventReplies) {
      const attendeeNames: string[] = [];
      const attendeeTypes: Array<AttendType> = [];
      for (const ateendee of reply.attendees) {
        attendeeNames.push(ateendee.name);
        attendeeTypes.push(ateendee.attendanceType);
      }

      let item: EventRepliesTableModel = {
        eventRepliesSortKey: reply.eventRepliesSortKey,
        residentName: reply.residentName,
        residentRoom: reply.residentRoom,
        familyName: reply.familyName,
        familyRelation: reply.familyRelation,
        attendeeNames: attendeeNames,
        attendeeTypes: attendeeTypes
      };

      eventReplyItems.push(item);
    }

    return eventReplyItems;
  }

  /**
   * ### イベント返信編集画面へ遷移する
   */
  public gotoEventRepliesEdit(item: EventRepliesTableModel): void {
    this.showEventRepliesModal = false;
    this.$router.push({ name: PcUrl.EventReplyEdit, params: { id: `${item.eventRepliesSortKey}` } });
    return;
  }

  /**
   * ### イベント返信削除の確認モーダルを表示する
   */
  public showDeleteEventRepliesConfirm(item: EventRepliesTableModel): void {
    this.deleteEventReplyMessage = `イベント申込み情報を削除しますか？`;
    if (item.eventRepliesSortKey) {
      this.deleteTargetEventReplySortKey = item.eventRepliesSortKey;
    }
    this.showEventReplyDeleteModal = true;
    return;
  }

  /**
   * ### イベント返信を削除する
   */
  public async deleteEventReply(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    await this.eventRepliesService.delete(userStore, this.deleteTargetEventReplySortKey);

    // 申込み状況の件数をデクリメントするだけで再リクエストはしない
    this.items = this.decrementAttendeeCount(this.items.concat(), this.eventRepliesEventId);

    this.showEventRepliesModal = false;
    this.showEventReplyDeleteSuccessModal = true;
    this.isBusy = false;

    return;
  }

  public decrementAttendeeCount(items: Array<EventTableItem>, targetEventId: number): Array<EventTableItem> {
    for (const item of items) {
      if (item.id == targetEventId) {
        item.attendeeCount = item.attendeeCount ? item.attendeeCount - 1 : item.attendeeCount;
        break;
      }
    }
    return items;
  }

  /**
   * ### CSVを出力する
   */
  public exportCsv(eventReply: { items: Array<EventRepliesTableModel>; title: string }): void {
    const csv: string = this.createEventRepliesCsv(eventReply.items);
    this.downloadCsv(csv, "HitomeQコネクト-イベント参加者一覧-" + eventReply.title + ".csv");
    return;
  }

  /**
   * ### イベント申込のCSVを作成する
   */
  public createEventRepliesCsv(eventReplyItems: Array<EventRepliesTableModel>): string {
    let csv: string = "\ufeff" + "利用者名（部屋名）,予約家族名（続柄）,参加者名,参加種別\n";

    for (const item of eventReplyItems) {
      const residentName: string = `${item.residentName}（${item.residentRoom}）`;
      const familyName: string = `${item.familyName}（${item.familyRelation}）`;

      for (let i = 0; i < item.attendeeNames.length; i++) {
        const attendeeName: string = item.attendeeNames[i];
        const attendeeType: string = this.convertAttendeeTypeName(item.attendeeTypes[i]);

        const line: string = `${residentName},${familyName},${attendeeName},${attendeeType}\n`;
        csv += line;
      }
    }

    return csv;
  }

  /**
   * ### イベント申込種別から文字列に変換して返す
   */
  public convertAttendeeTypeName(attendeeType: AttendType): string {
    let attendeeTypeName: string = "";
    switch (attendeeType) {
      case AttendType.LOCAL: {
        attendeeTypeName = "現地参加";
        break;
      }
      case AttendType.ONLINE: {
        attendeeTypeName = "オンライン参加";
        break;
      }
    }

    return attendeeTypeName;
  }

  /**
   * ### CSVファイルをダウンロードさせる
   *
   * - CSVの内容からblobファイルを作成する
   * - blobファイルの種別はtext/csv
   * - a要素を作ってそれを疑似的にクリックさせることでダウンロードさせる
   */
  public downloadCsv(csv: string, fileName: string): void {
    const blob: Blob = new Blob([csv], { type: "text/csv" });

    let link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.click();

    return;
  }

  /**
   * ### イベント返信登録画面へ遷移する
   */
  public gotoEventReplyRegister(): void {
    this.showEventRepliesModal = false;
    this.$router.push({ name: PcUrl.EventReplyRegister, params: { id: `${this.eventRepliesEventId}` } });
    return;
  }

  /**
   * ### ライブ配信を開始する
   *
   * - 新規ウィンドウでライブ配信を表示する
   */
  public startLive(item: EventTableItem): void {
    const url: string = this.createLiveUrl(item);
    window.open(url, "_blank");
    return;
  }

  /**
   * ### ライブ配信URLを作成する
   */
  public createLiveUrl(item: EventTableItem): string {
    const onlineEventMainUrl: string | undefined = process.env.VUE_APP_ONLINE_EVENT_URL;

    const facilityId: number = userStore.facility.id;
    const eventId: number = item.id;
    const userId: string = userStore.loginUser.userId;
    const meetingToken: string = item.meetingToken;

    const liveUrl: string = `${onlineEventMainUrl}?facilityId=${facilityId}&eventId=${eventId}&userId=${userId}&meetingToken=${meetingToken}`;

    return liveUrl;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

#event-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    .error-message {
      padding-left: 20px;
    }
    .add-btn {
      width: 64px;
    }
  }

  .box-body {
    .tab-title {
      height: 25px;
    }
    .refresh-icon {
      @include svg-icon("../../../assets/refresh.svg", $font-base-color, 20px, 20px);
      opacity: 0.5;
      text-shadow: 0 1px 0 #fff;
      cursor: pointer;
    }
    .refresh-icon:hover {
      opacity: 0.75;
    }
  }
}
</style>
